import { TFunction } from 'i18next'

import type { DcaErrorCode } from '@web/extension-services/background/controllers/dca/errors'

/** User-facing text of a DcaController error code. */
export const getDcaErrorText = (code: DcaErrorCode, t: TFunction): string => {
  switch (code) {
    case 'UNSUPPORTED_TOKEN':
      return t('This token is not supported for DCA.')
    case 'SAME_TOKEN':
      return t('Choose two different tokens.')
    case 'INVALID_AMOUNT':
      return t('Enter an amount greater than 0.')
    case 'AMOUNT_BELOW_MINIMUM':
      return t(
        'Each execution is below the minimum value. Increase the amount or use fewer executions.'
      )
    case 'PRICE_UNAVAILABLE':
      return t('The token price is unavailable right now. Please try again.')
    case 'INTERVAL_OUT_OF_RANGE':
      return t('The interval is outside the allowed range.')
    case 'EXECUTIONS_OUT_OF_RANGE':
      return t('The number of executions is outside the allowed range.')
    case 'SLIPPAGE_TOO_LOW':
      return t('Slippage is below the allowed minimum.')
    case 'SLIPPAGE_TOO_HIGH':
      return t('Slippage is above the allowed maximum.')
    case 'SLIPPAGE_BELOW_ROUTE_MINIMUM':
      return t('Slippage is below what this route needs right now. Review the order again.')
    case 'INSUFFICIENT_LIQUIDITY':
      return t(
        'Not enough liquidity to DCA this amount. Use a smaller amount per buy (more executions) or another token.'
      )
    case 'EXECUTION_FEE_TOO_HIGH':
      return t('The amount per execution is too small for the execution fee.')
    case 'INVALID_SCHEDULE':
      return t('The schedule is not valid. Pick a later start time or a shorter plan.')
    case 'DCA_DISABLED':
      return t('DCA is temporarily unavailable.')
    case 'NETWORK_ERROR':
      return t('Could not reach the DCA service. Check your connection and try again.')
    case 'FEE_CHANGED':
      return t('The ZKX fee changed. Review the order again.')
    case 'SCHEDULE_EXPIRED':
      return t('The schedule is no longer valid. Review the order again.')
    case 'INVALID_TOKEN_PAIR':
      return t('This token pair is not supported for DCA.')
    case 'DCA_PAUSED':
      return t('DCA is paused right now. Try again later.')
    case 'PLAN_REJECTED':
      return t(
        'The DCA service returned a transaction that does not match your order, so it was not signed.'
      )
    case 'TX_REVERTED':
      return t('The transaction failed on-chain. Review the order and try again.')
    case 'TX_TIMEOUT':
      return t('The transaction is taking too long to confirm. Check Activity before trying again.')
    case 'SMART_ACCOUNT':
      return t('DCA is available for basic accounts only for now.')
    case 'ORDER_NOT_FOUND':
      return t('This DCA order was not found.')
    case 'NOT_ORDER_OWNER':
      return t('This DCA order belongs to another account.')
    case 'ORDER_STATUS_CHANGED':
      return t('This DCA order has already finished or been cancelled. Refresh to see its status.')
    case 'ORDER_EXPIRED':
      return t('This DCA order has expired. You can still cancel it.')
    default:
      return t('Something went wrong while creating your DCA order.')
  }
}

/** Same codes, for loading or cancelling an order (the default sentence differs). */
export const getDcaManageErrorText = (code: DcaErrorCode, t: TFunction): string =>
  code === 'GENERIC' ? t('Something went wrong. Please try again.') : getDcaErrorText(code, t)
